/* eslint-disable @typescript-eslint/no-unused-vars */

import { HttpClient, HttpHeaders, HttpParams, HttpResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, catchError, combineLatest, map, of, switchMap, throwError } from 'rxjs';
import { AdminOfferOverviewDTO } from 'src/app/models/admin-models/DTOs/AdminOfferOverviewDTO';
import { CompanyDTO } from 'src/app/models/admin-models/DTOs/CompanyDTO';
import { EmailInfoDTO } from 'src/app/models/admin-models/DTOs/EmailInfoDTO';
import { SeriesListDTO } from 'src/app/models/admin-models/interfaces/AdminInterfaces';
import { Company } from 'src/app/models/admin-models/views/Company';
import { EmailInfo } from 'src/app/models/admin-models/views/EmailInfo';
import { statusEnum } from 'src/app/models/enums/ProductEnums';
import { CompanySummaryDTO } from 'src/app/models/generic-models/DTOs/CompanySummaryDTO';
import { FrontendTextDTO } from 'src/app/models/generic-models/DTOs/FrontendTextDTO';
import { OfferDTO } from 'src/app/models/generic-models/DTOs/OfferDTO';
import { LoginResponse, LoginUser, UserDTO, UserRoleENUM } from 'src/app/models/generic-models/DTOs/UserDTO';
import { CompanySummary } from 'src/app/models/generic-models/views/CompanySummary';
import { Offer } from 'src/app/models/generic-models/views/Offer';
import { PriceBreakdown } from 'src/app/models/generic-models/views/PriceBreakdown';
import { AttributeDTO } from 'src/app/models/product-models/DTOs/AttributeDTO';
import { AttributeValueDTO } from 'src/app/models/product-models/DTOs/AttributeValueDTO';
import { CategoryDTO } from 'src/app/models/product-models/DTOs/CategoryDTO';
import { FloorplanDTO } from 'src/app/models/product-models/DTOs/FloorplanDTO';
import { LocalizedTextDTO } from 'src/app/models/product-models/DTOs/LocalizedTextDTO';
import { PriceDataDTO } from 'src/app/models/product-models/DTOs/PriceDataDTO';
import { ProductBlueprintDTO } from 'src/app/models/product-models/DTOs/ProductBlueprintDTO';
import { ProductDTO } from 'src/app/models/product-models/DTOs/ProductDTO';
import { ProductRelationShipDTO } from 'src/app/models/product-models/DTOs/ProductRelationshipDTO';
import { ProductRelationshipGroupDTO } from 'src/app/models/product-models/DTOs/ProductRelationshipGroupDTO';
import { ProductWithImageDTO } from 'src/app/models/product-models/DTOs/ProductWithImageDTO';
import { SeriesDTO } from 'src/app/models/product-models/DTOs/SeriesDTO';
import { SeriesSummaryDTO } from 'src/app/models/product-models/DTOs/SeriesSummaryDTO';
import { Attribute } from 'src/app/models/product-models/views/Attribute';
import { AttributeValue } from 'src/app/models/product-models/views/AttributeValue';
import { Category } from 'src/app/models/product-models/views/Category';
import { ProductBlueprint } from 'src/app/models/product-models/views/ProductBlueprint';
import { ProductRelationship } from 'src/app/models/product-models/views/ProductRelationship';
import { ProductRelationshipGroup } from 'src/app/models/product-models/views/ProductRelationshipGroup';
import { SeriesSummary } from 'src/app/models/product-models/views/SeriesSummary';
import { flattenSteps } from 'src/app/models/utils';
import { Step, StepDTO } from 'src/app/step-modules/step/models/step';
import { removeIds } from 'src/app/utils/Utils';
import { environment } from 'src/environments/environment';

export const LANGUAGE_HEADER = 'configurator-language';

@Injectable()
export class BackendService {
  constructor(private http: HttpClient) {}

  public getCompanies$(): Observable<CompanyDTO[]> {
    const url = `${environment.APIURL}/companies`;
    return this.http.get<CompanyDTO[]>(url);
  }

  public getSeriesSummaries$(companyId: string): Observable<SeriesSummaryDTO[]> {
    const url = `${environment.APIURL}/companies/${companyId}/series`;
    //const url = `./assets/series.json`;
    return this.http.get<SeriesSummaryDTO[]>(url);
  }

  public getSeriesSummariesList$(
    companyId: string,
    currentPage: number,
    pageSize: number,
    keyword: string,
  ): Observable<SeriesSummaryDTO[]> {
    let keywordQuery = `&keyword=${keyword}`;
    if (keyword === '') keywordQuery = '';

    const url = `${environment.APIURL}/companies/${companyId}/series?page=${currentPage}&size=${pageSize}${keywordQuery}`;
    return this.http.get<SeriesSummaryDTO[]>(url);
  }

  public getRootStep$() {
    return this.http.get<StepDTO>('/assets/rootStep.json').pipe(
      map((stepDTO) => Step.fromDTO(stepDTO)),

      catchError((err) => {
        return throwError(() => err);
      }),
    );
  }

  public getCompanySteps$(companyId: string) {
    /* let url = '/assets/companySteps.json';
    switch (companyId) {
      case '3': {
        url = '/assets/klunSteps.json';
        break;
      }
      case '201': {
        url = '/assets/inajSteps.json';
        break;
      }
      case '202': {
        url = '/assets/inajSteps.json';
        break;
      }
      default: {
        break;
      }
    } */

    const url = `${environment.APIURL}/steps/contextType/COMPANY/contextId/${companyId}`;

    return this.http.get<StepDTO[]>(url).pipe(
      switchMap((stepDTO) => {
        const urlStep = `${environment.APIURL}/steps/${stepDTO[0].id}`;

        return this.http.get<StepDTO>(urlStep);
      }),
      map((stepDTO) => stepDTO.steps),
      map((stepDTOs) => stepDTOs.map((stepDTO) => Step.fromDTO(stepDTO))),

      catchError((err) => {
        return throwError(() => err);
      }),
    );
  }

  public getSeriesSteps$(seriesId: string) {
    /* let url = '/assets/seriesSteps.json';
    switch (companyId) {
      case '701': {
        url = '/assets/klunSeriesSteps.json';
        break;
      }
      case '4429': {
        url = '/assets/klunSeriesSteps.json';
        break;
      }
      case '5905': {
        url = '/assets/inajSeriesSteps.json';
        break;
      }
      case '5985': {
        url = '/assets/inajSeriesSteps.json';
        break;
      }
      default: {
        break;
      }
    } */
    const url = `${environment.APIURL}/steps/contextType/SERIES/contextId/${seriesId}`;
    return this.http.get<StepDTO[]>(url).pipe(
      switchMap((stepDTO) => {
        const urlStep = `${environment.APIURL}/steps/${stepDTO[0].id}`;

        return this.http.get<StepDTO>(urlStep);
      }),
      map((stepDTO) => stepDTO.steps),
      map((stepDTOs) => stepDTOs.map((stepDTO) => Step.fromDTO(stepDTO))),

      catchError((err) => {
        return throwError(() => err);
      }),
    );
  }

  public getProductBlueprints$(
    companyId: string,
    currentPage: number,
    pageSize: number,
    keyword: string,
  ): Observable<HttpResponse<ProductBlueprintDTO[]>> {
    let keywordQuery = `&keyword=${keyword}`;
    if (keyword === '') keywordQuery = '';

    const url = `${environment.APIURL}/companies/${companyId}/product-blueprints?page=${currentPage}&size=${pageSize}${keywordQuery}`;
    return this.http.get<ProductBlueprintDTO[]>(url, { observe: 'response' });
  }

  public getSerieSummary$(companyId: string, seriesId: string): Observable<SeriesSummaryDTO> {
    const url = `${environment.APIURL}/companies/${companyId}/series/${seriesId}`;
    return this.http.get<SeriesSummaryDTO>(url);
  }

  public getProduct$(companyId: string, productId: string) {
    const url = `${environment.APIURL}/companies/${companyId}/products/${productId}`;
    //const url = `/assets/exampleProduct.json`;
    return this.http.get<ProductDTO>(url);
  }

  public getProductBlueprint$(companyId: string, blueprintId: string): Observable<ProductBlueprintDTO> {
    const url = `${environment.APIURL}/companies/${companyId}/product-blueprints/${blueprintId}`;
    return this.http.get<ProductBlueprintDTO>(url);
  }

  public createSerie$(companyId: string, series: SeriesSummary) {
    const url = `${environment.APIURL}/companies/${companyId}/series`;
    //series.thumbnailUrl = 'https://4th.vision/wp-content/uploads/2023/03/logo.webp'; // TODO: Upload pic
    series.isPrivate = !series.isPrivate;
    return this.http.post<SeriesSummaryDTO>(url, removeIds(series), { observe: 'response', responseType: 'json' });
  }

  public editSerie$(companyId: string, id: string, series: SeriesSummary) {
    const url = `${environment.APIURL}/companies/${companyId}/series/${id}`;
    //series.thumbnailUrl = 'https://4th.vision/wp-content/uploads/2023/03/logo.webp'; // TODO: Upload pic
    series.isPrivate = !series.isPrivate;

    const data = {
      name: series.name,
      thumbnailUrl: series.thumbnailUrl,
      isPrivate: series.isPrivate,
      configurations: series.configurations.map((serie) => Number(serie)),
      categories: series.categories.map((category) => category.toDTO()),
    };

    return this.http.put<SeriesSummaryDTO>(url, removeIds(data), { observe: 'response', responseType: 'json' });
  }

  public deleteSerie$(companyId: string, id: string) {
    const url = `${environment.APIURL}/companies/${companyId}/series/${id}`;
    return this.http.delete(url, { observe: 'response', responseType: 'json' });
  }

  public getCategory$(companyId: string, seriesId: string, categoryId: string) {
    const url = `${environment.APIURL}/companies/${companyId}/series/${seriesId}/categories/${categoryId}`;
    return this.http.get<CategoryDTO>(url);
  }

  public getCategories$(companyId: string, seriesId: string, currentPage: number, pageSize: number) {
    const pageString = `page=${currentPage}`;
    const sizeString = `&size=${pageSize}`;
    const url = `${environment.APIURL}/companies/${companyId}/series/${seriesId}/categories?${pageString}${sizeString}`;
    return this.http.get<CategoryDTO[]>(url);
  }

  public createCategory$(companyId: string, seriesId: string, category: Category) {
    const url = `${environment.APIURL}/companies/${companyId}/series/${seriesId}/categories`;
    return this.http.post<CategoryDTO>(url, category.toDTO(), { observe: 'response', responseType: 'json' });
  }

  public editCategory$(companyId: string, seriesId: string, id: string, category: Category) {
    const url = `${environment.APIURL}/companies/${companyId}/series/${seriesId}/categories/${id}`;
    return this.http.put<CategoryDTO>(url, removeIds(category.toDTO()), { observe: 'response', responseType: 'json' });
  }

  public deleteCategory$(companyId: string, seriesId: string, id: string) {
    const url = `${environment.APIURL}/companies/${companyId}/series/${seriesId}/categories/${id}`;
    return this.http.delete(url, { observe: 'response', responseType: 'json' });
  }

  public getProductRelationshipGroups(
    companyId: string,
    productBlueprintId: string,
    currentPage: number,
    pageSize: number,
  ) {
    const pageString = `page=${currentPage}`;
    const sizeString = `&size=${pageSize}`;
    const url = `${environment.APIURL}/companies/${companyId}/product-blueprints/${productBlueprintId}/product-relationship-groups?${pageString}${sizeString}`;
    return this.http.get<ProductRelationshipGroupDTO[]>(url, { observe: 'response', responseType: 'json' });
  }

  public getProductRelationshipGroup(
    companyId: string,
    productBlueprintId: string,
    productRelationshipGroupId: string,
  ) {
    const url = `${environment.APIURL}/companies/${companyId}/product-blueprints/${productBlueprintId}/product-relationship-groups/${productRelationshipGroupId}`;
    return this.http.get<ProductRelationshipGroupDTO>(url);
  }

  public createProductRelationshipGroup(
    companyId: string,
    productBlueprintId: string,
    productRelationshipGroup: ProductRelationshipGroup,
  ) {
    const url = `${environment.APIURL}/companies/${companyId}/product-blueprints/${productBlueprintId}/product-relationship-groups`;
    return this.http.post<ProductRelationshipGroupDTO>(url, removeIds(productRelationshipGroup.toDTO()));
  }

  public editProductRelationshipGroup(
    companyId: string,
    productBlueprintId: string,
    productRelationshipGroupId: string,
    productRelationshipGroup: ProductRelationshipGroup,
  ) {
    const url = `${environment.APIURL}/companies/${companyId}/product-blueprints/${productBlueprintId}/product-relationship-groups/${productRelationshipGroupId}`;
    return this.http.put<ProductRelationshipGroupDTO>(url, productRelationshipGroup.toDTO());
  }

  public deleteProductRelationshipGroup(
    companyId: string,
    productBlueprintId: string,
    productRelationshipGroupId: string,
  ) {
    const url = `${environment.APIURL}/companies/${companyId}/product-blueprints/${productBlueprintId}/product-relationship-groups/${productRelationshipGroupId}`;
    return this.http.delete(url);
  }

  public createProductRelationship(
    companyId: string,
    productBlueprintId: string,
    productRelationshipGroupId: string,
    productRelationship: ProductRelationship,
  ) {
    const url = `${environment.APIURL}/companies/${companyId}/product-blueprints/${productBlueprintId}/product-relationship-groups/${productRelationshipGroupId}/product-relationships`;
    return this.http.post<ProductRelationShipDTO>(url, removeIds(productRelationship.toDTO()));
  }

  public getProductRelationship(
    companyId: string,
    productBlueprintId: string,
    productRelationshipGroupId: string,
    productRelationshipId: string,
  ) {
    const url = `${environment.APIURL}/companies/${companyId}/product-blueprints/${productBlueprintId}/product-relationship-groups/${productRelationshipGroupId}/product-relationships/${productRelationshipId}`;
    return this.http.get<ProductRelationShipDTO>(url);
  }

  public getProductRelationships(companyId: string, productBlueprintId: string, productRelationshipGroupId: string) {
    const url = `${environment.APIURL}/companies/${companyId}/product-blueprints/${productBlueprintId}/product-relationship-groups/${productRelationshipGroupId}/product-relationships}`;
    return this.http.get<ProductRelationShipDTO>(url);
  }

  public deleteProductRelationship(
    companyId: string,
    productBlueprintId: string,
    productRelationshipGroupId: string,
    productRelationshipId: string,
  ) {
    const url = `${environment.APIURL}/companies/${companyId}/product-blueprints/${productBlueprintId}/product-relationship-groups/${productRelationshipGroupId}/product-relationships/${productRelationshipId}`;
    return this.http.delete<ProductRelationShipDTO>(url);
  }

  public editProductRelationship(
    companyId: string,
    productBlueprintId: string,
    productRelationshipGroupId: string,
    productRelationshipId: string,
    productRelationship: ProductRelationship,
  ) {
    const url = `${environment.APIURL}/companies/${companyId}/product-blueprints/${productBlueprintId}/product-relationship-groups/${productRelationshipGroupId}/product-relationships/${productRelationshipId}`;
    return this.http.put<ProductRelationShipDTO>(url, removeIds(productRelationship.toDTO()));
  }

  public getSeries$(companyId: string, seriesId: string): Observable<SeriesDTO> {
    //const url = `./assets/exampleSeries.json`;
    const url = `${environment.APIURL}/companies/${companyId}/series/${seriesId}`;
    return this.http.get<SeriesDTO>(url);
  }

  public getHardcodedSeries$(companyId: string, seriesId: string): Observable<SeriesDTO> {
    const url = `./assets/exampleSeries.json`;
    return this.http.get<SeriesDTO>(url);
  }

  public createBlueprint$(companyId: string, blueprint: ProductBlueprint, image: File | null, model: File | null) {
    const url = `${environment.APIURL}/companies/${companyId}/product-blueprints`;
    //TODO: popravi to, dodaj flipping axis
    const data = {
      dimensions: blueprint.dimensions === null ? undefined : blueprint.dimensions,
      modelUrl: blueprint.modelUrl,
      thumbnailUrl: blueprint.thumbnailUrl,
      externalId: blueprint.externalId,
      infoText: blueprint.infoText,
      /* basePrice: blueprint.basePrice, */
    };

    /*let imageObservable$ = of({});
    if (image) imageObservable$ = this.uploadFile$(companyId, image.name, image);

    let modelObservable$ = of({});
    if (model) modelObservable$ = this.uploadFile$(companyId, model.name, model);

    combineLatest([imageObservable$, modelObservable$])
      .pipe(first())
      .subscribe(([image, model]) => {
        console.log(image, model);
        return this.http.post<ProductBlueprintDTO>(url, data, { observe: 'response', responseType: 'json' });
      });*/

    return this.http.post<ProductBlueprintDTO>(url, data, { observe: 'response', responseType: 'json' });
  }

  public editBlueprint$(companyId: string, id: string, blueprint: ProductBlueprint) {
    const url = `${environment.APIURL}/companies/${companyId}/product-blueprints/${id}`;
    return this.http.put<ProductBlueprintDTO>(url, blueprint.toDTO(), { observe: 'response', responseType: 'json' });
  }

  public deleteBlueprint$(companyId: string, blueprintId: string) {
    const url = `${environment.APIURL}/companies/${companyId}/product-blueprints/${blueprintId}`;
    return this.http.delete(url, { observe: 'response', responseType: 'json' });
  }

  public getSeriesProductBlueprints$(
    companyId: string,
    seriesId: string,
    statuses: statusEnum[] = [],
  ): Observable<ProductBlueprintDTO[]> {
    //const url = `./assets/productBlueprints.json`;
    //TO JE KER ČE GETAMO ARHIVIRANO SERIJO MORE GETAT TUD ARHIVIRANE BLUEPRINTE
    statuses = [statusEnum.Archived, statusEnum.Active];
    const url = `${environment.APIURL}/companies/${companyId}/series/${seriesId}/product-blueprints`;
    let parameters = new HttpParams();

    // Append each status to the query parameters
    for (const status of statuses) {
      parameters = parameters.append('status', status);
    }
    return this.http.get<ProductBlueprintDTO[]>(url, { params: parameters });
  }

  public getFloorplan$(floorplanId: string): Observable<FloorplanDTO> {
    const url = `./assets/floorplanExample.json`;
    return this.http.get<FloorplanDTO>(url);
  }

  public getFloorplans$(seriesId: string): Observable<FloorplanDTO[]> {
    const url = `./assets/floorplanExample.json`;
    return this.http.get<FloorplanDTO[]>(url);
  }

  public getSeriesInfo$(companyId: string): Observable<SeriesListDTO> {
    //const url = `${environment.APIURL}/companies/` + companyId + '/series';
    const url = `./assets/seriesList.json`;
    return this.http.get<SeriesListDTO>(url);
  }

  public getOfferOverviewInfo$(
    companyId: string,
    currentPage: number,
    pageSize: number,
    series: string,
    statuses: string[],
    keyword: string,
    startDate: string,
    endDate: string,
  ): Observable<HttpResponse<AdminOfferOverviewDTO[]>> {
    let keywordQuery = `&keyword=${keyword}`;
    if (keyword === '') keywordQuery = '';

    const pageString = currentPage ? `page=${currentPage}` : '';
    const sizeString = pageSize ? `&size=${pageSize}` : '';
    const seriesString = series ? `&series=${series}` : '';
    const statusString =
      statuses && statuses.length
        ? statuses.reduce((accumulator, status) => accumulator + `&status=${encodeURIComponent(status)}`, '')
        : '';
    const startDateString = startDate ? `&startDate=${startDate}` : '';
    const endDateString = endDate ? `&endDate=${endDate}` : '';

    const url = `${environment.APIURL}/companies/${companyId}/offers?${pageString}${sizeString}${seriesString}${statusString}${startDateString}${endDateString}${keywordQuery}`;
    return this.http.get<AdminOfferOverviewDTO[]>(url, { observe: 'response' });
  }

  public getOffer$(companyId: string, id: string): Observable<OfferDTO> {
    const url = `${environment.APIURL}/companies/${companyId}/offers/${id}`;
    //const url = `./assets/offerExample.json`;
    return this.http.get<OfferDTO>(url);
  }

  public getCompaniesInfo$(currentPage: number, pageSize: number, keyword: string): Observable<CompanyDTO[]> {
    let keywordQuery = `&keyword=${keyword}`;
    if (keyword === '') keywordQuery = '';

    const url = `${environment.APIURL}/companies?page=${currentPage}&size=${pageSize}${keywordQuery}`;

    return this.http.get<CompanyDTO[]>(url);
  }

  public getAttributes$(
    companyId: string,
    blueprintId: string,
    currentPage: number,
    pageSize: number,
    keyword: string,
  ): Observable<AttributeDTO[]> {
    let keywordQuery = `&keyword=${keyword}`;
    if (keyword === '') keywordQuery = '';

    const url = `${environment.APIURL}/companies/${companyId}/product-blueprints/${blueprintId}/attributes?page=${currentPage}&size=${pageSize}${keywordQuery}`;
    return this.http.get<AttributeDTO[]>(url);
  }

  public getAttributeValues$(
    attributeId: string,
    currentPage: number,
    pageSize: number,
    keyword: string,
  ): Observable<AttributeValueDTO[]> {
    let keywordQuery = `&keyword=${keyword}`;
    if (keyword === '') keywordQuery = '';

    const url = `${environment.APIURL}/attributes/${attributeId}/attribute-values?page=${currentPage}&size=${pageSize}${keywordQuery}`;
    return this.http.get<AttributeValueDTO[]>(url);
  }

  public getCompany$(id: string): Observable<CompanyDTO> {
    const url = `${environment.APIURL}/companies/${id}`;
    //const url = `/assets/exampleCompany.json`;
    return this.http.get<CompanyDTO>(url);
  }

  public getAttribute$(aid: string) {
    const url = `${environment.APIURL}/attributes/${aid}`;
    return this.http.get<AttributeDTO>(url);
  }

  public getAttributeValue$(aid: string, vid: string) {
    const url = `${environment.APIURL}/attributes/${aid}/attribute-values/${vid}`;
    return this.http.get<AttributeValueDTO>(url);
  }

  public createAttribute$(cid: string, bid: string, attribute: Attribute) {
    const url = `${environment.APIURL}/companies/${cid}/product-blueprints/${bid}/attributes`;
    return this.http.post<AttributeDTO>(url, removeIds(attribute.toDTO()), {
      observe: 'response',
      responseType: 'json',
    });
  }

  public editAttribute$(aid: string, attribute: Attribute) {
    const url = `${environment.APIURL}/attributes/${aid}`;
    return this.http.put<AttributeDTO>(url, removeIds(attribute.toDTO()), {
      observe: 'response',
      responseType: 'json',
    });
  }

  public deleteAttribute$(aid: string) {
    const url = `${environment.APIURL}/attributes/${aid}`;
    return this.http.delete(url, { observe: 'response', responseType: 'json' });
  }

  public createAttributeValue$(aid: string, value: AttributeValue) {
    const url = `${environment.APIURL}/attributes/${aid}/attribute-values`;
    return this.http.post<AttributeValueDTO>(url, removeIds(value.toDTO()), {
      observe: 'response',
      responseType: 'json',
    });
  }

  public editAttributeValue$(vid: string, value: AttributeValue) {
    const url = `${environment.APIURL}/attribute-values/${vid}`;
    return this.http.put<AttributeValueDTO>(url, removeIds(value.toDTO()), {
      observe: 'response',
      responseType: 'json',
    });
  }

  public deleteAttributeValue$(vid: string) {
    const url = `${environment.APIURL}/attribute-values/${vid}`;
    return this.http.delete(url, { observe: 'response', responseType: 'json' });
  }

  public editCompany$(company: Company, id: string) {
    const url = `${environment.APIURL}/companies/${id}`;
    return this.http.put<CompanyDTO>(url, company, { observe: 'response', responseType: 'json' });
  }

  public createCompany$(company: Company) {
    const url = `${environment.APIURL}/companies`;
    return this.http.post<CompanyDTO>(url, removeIds(company), { observe: 'response', responseType: 'json' });
  }

  public deleteCompany$(id: string) {
    const url = `${environment.APIURL}/companies/${id}`;
    return this.http.delete(url, { observe: 'response', responseType: 'json' });
  }

  public getWorkers$(cid: string, currentPage: number, pageSize: number, keyword: string): Observable<UserDTO[]> {
    let keywordQuery = `&keyword=${keyword}`;
    if (keyword === '') keywordQuery = '';

    const url = `${environment.APIURL}/companies/${cid}/users?page=${currentPage}&size=${pageSize}${keywordQuery}`;
    return this.http.get<UserDTO[]>(url);
  }

  public getWorker$(id: string, cid: string): Observable<UserDTO> {
    const url = `${environment.APIURL}/companies/${cid}/users/${id}`;
    return this.http.get<UserDTO>(url);
  }

  public editWorker$(id: string, cid: string, username: string, role: UserRoleENUM): Observable<UserDTO> {
    const url = `${environment.APIURL}/companies/${cid}/users/${id}`;

    const data = {
      username: username,
      roles: role,
    };

    return this.http.put<UserDTO>(url, data);
  }

  public addWorker$(cid: string, username: string, password: string, role: UserRoleENUM) {
    const url = `${environment.APIURL}/companies/${cid}/users`;

    const data = {
      username: username,
      password: password,
      roles: role,
    };

    return this.http.post(url, data, { observe: 'response', responseType: 'json' });
  }

  public resetPassword$(wid: string, cid: string, resetPassword: boolean, password: string) {
    const url = `${environment.APIURL}/companies/${cid}/users/${wid}/pass`;

    const data = {
      password: password,
      resetPassword: resetPassword,
    };

    return this.http.post(url, data, { observe: 'response', responseType: 'json' });
  }

  public deleteWorker$(id: string, cid: string) {
    const url = `${environment.APIURL}/companies/${cid}/users/${id}`;
    return this.http.delete<UserDTO>(url, { observe: 'response', responseType: 'json' });
  }

  public updateParameters$(id: number, cid: string, priceBreakdown: PriceBreakdown): Observable<PriceBreakdown> {
    const url = `${environment.APIURL}/singleUser.json`;
    return this.http.put<PriceBreakdown>(url, priceBreakdown);
  }

  public getCompanyEmailInfo$(id: string): Observable<EmailInfoDTO> {
    const url = `${environment.APIURL}/companies/${id}/mail/mail-info`;
    return this.http.get<EmailInfoDTO>(url);
  }

  public updateCompanyEmailInfo$(id: string, info: EmailInfo): Observable<EmailInfoDTO> {
    const url = `${environment.APIURL}/companies/${id}/mail/mail-info`;
    return this.http.post<EmailInfoDTO>(url, info);
  }

  public sendTestEmail$(id: string, text: string): Observable<EmailInfoDTO> {
    const url = `${environment.APIURL}/companies/${id}/mail/mail-send`;
    return this.http.post<EmailInfoDTO>(url, { email: text });
  }

  public submitOffer$(id: string, offer: Offer, language: string) {
    const url = `${environment.APIURL}/companies/${id}/offers`;
    return this.http.post<OfferDTO>(url, removeIds(offer), { headers: { [LANGUAGE_HEADER]: language } });
  }

  public submitOfferTemporary$(id: string, offer: OfferDTO, language: string) {
    const url = `${environment.APIURL}/companies/${id}/offers/temporary`;
    return this.http.post<OfferDTO>(url, removeIds(offer), { headers: { [LANGUAGE_HEADER]: language } });
  }

  public updateOffer$(id: string, offer: Offer, olfOfferId: string, language: string) {
    const url = `${environment.APIURL}/companies/${id}/offers/${olfOfferId}/versions`;
    return this.http.post<OfferDTO>(url, removeIds(offer), { headers: { [LANGUAGE_HEADER]: language } });
  }

  public getDetailsById$(id: string, showCompanyInfo: boolean): Observable<OfferDTO> {
    const url = `${environment.APIURL}/offerExample.json`;
    //const url = `./assets/offerExample.json`;
    return this.http.get<OfferDTO>(url);
  }

  public getUser$(): Observable<LoginUser> {
    return this.http.get<LoginUser>(`${environment.APIURL}/login`);
  }

  public getHardcodedCompanies$() {
    return this.http.get<CompanyDTO[]>('/assets/companies.json');
  }

  public getCompanySummaries$() {
    const url = `${environment.APIURL}/companies/company-summaries`;

    return this.http.get<CompanySummaryDTO[]>(url);
  }

  public getCompanyDTOBySlug$(slug: string): Observable<CompanyDTO> {
    const url = `${environment.APIURL}/companies/slug/${slug}`;
    //const url = `/assets/exampleCompany.json`;
    return this.http.get<CompanyDTO>(url);
  }

  public getCompanyBySlug$(slug: string) {
    return this.getCompanyDTOBySlug$(slug).pipe(map((companyDTO) => Company.fromDTO(companyDTO)));
  }

  public getCompanySummaryDTOBySlug$(slug: string) {
    const url = `${environment.APIURL}/companies/slug/${slug}/company-summaries`;
    //const url = `/assets/exampleCompany.json`;
    return this.http.get<CompanySummaryDTO>(url);
  }

  public getCompanySummarySlug$(slug: string) {
    return this.getCompanySummaryDTOBySlug$(slug).pipe(
      map((companySummaryDTO) => CompanySummary.fromDTO(companySummaryDTO)),
    );
  }

  public getCompanySummaryDTOById$(id: string) {
    const url = `${environment.APIURL}/companies/${id}/company-summaries`;
    //const url = `/assets/exampleCompany.json`;
    return this.http.get<CompanySummaryDTO>(url);
  }

  public getCompanySummaryById$(id: string) {
    return this.getCompanySummaryDTOById$(id).pipe(
      map((companySummaryDTO) => CompanySummary.fromDTO(companySummaryDTO)),
    );
  }

  public login$(username: string, password: string): Observable<LoginResponse | null> {
    const url = `${environment.APIURL}/login`;

    return this.http
      .post<LoginResponse>(url, JSON.stringify({ username: username, password: password }), {
        headers: { 'Content-Type': 'application/json' },
      })
      .pipe(
        catchError(() => {
          return of(null); // Return null in case of an error
        }),
      );
  }

  public editDetails$(
    id: string,
    status: string,
    name: string,
    email: string,
    phone: string,
    userComment: string,
    currentPacking: number,
    currentLogistic: number,
    currentMargin: number,
    currentCustoms: number,
    currentVAT: number,
    currentRVAT: number,
    currentDiscount: number,
  ): Observable<OfferDTO> {
    const url = `${environment.APIURL}/offerExample.json`;
    return this.http.get<OfferDTO>(url);
  }

  public uploadFile$(cid: string, name: string, file: File) {
    const url = `${environment.APIURL}/companies/${cid}/uploads`;
    const headers = new HttpHeaders({ enctype: 'multipart/form-data' });

    const data = new FormData();
    data.append('file', file);
    data.append('fileName', name);

    return this.http.post(url, data, { headers: headers });
  }

  public uploadARProduct$(productDTO: ProductDTO, companyId: string, productId?: string): Observable<ProductDTO> {
    /* if (productId) {
      const url = `${environment.APIURL}/companies/${companyId}/qr-products/${productId}`;
      return this.http.put<ProductDTO>(url, productDTO);
    } */
    const url = `${environment.APIURL}/companies/${companyId}/qr-products`;
    return this.http.post<ProductDTO>(url, removeIds(productDTO));
  }

  public getARProduct$(productId: string, companyId: string) {
    const url = `${environment.APIURL}/companies/${companyId}/products/${productId}`;
    //const url = `/assets/productExample.json`;
    return this.http.get<ProductDTO>(url);
  }

  public sendReminder$(companyId: string, offerId: string) {
    const url = `${environment.APIURL}/companies/${companyId}/offers/${offerId}/first-reminder`;
    return this.http.post(url, {}, { observe: 'response', responseType: 'text' });
  }

  public editOfferStatus$(companyId: string, offerId: string, status: string): Observable<string> {
    const url = `${environment.APIURL}/companies/${companyId}/offers/${offerId}/status`;
    return this.http.put<string>(url, { offerStatus: status });
  }

  public getProductBlueprintsPrices(companyId: string, productBlueprintId: string) {
    const url = `${environment.APIURL}/companies/${companyId}/product-blueprints/${productBlueprintId}/price`;
    return this.http.get<PriceDataDTO>(url);
  }

  public editPriceData(companyId: string, productBlueprintId: string, priceData: PriceDataDTO) {
    const url = `${environment.APIURL}/companies/${companyId}/product-blueprints/${productBlueprintId}/price`;
    return this.http.put<PriceDataDTO>(url, priceData);
  }

  public getSeriesConfigurations(companyId: string, seriesId: string) {
    const url = `${environment.APIURL}/companies/${companyId}/series/${seriesId}/configurations-2d-urls`;
    return this.http.get<ProductWithImageDTO[]>(url);
  }

  public getAllStepsFlattened() {
    const url = `${environment.APIURL}/steps?size=10000`;

    return this.http.get<StepDTO[]>(url).pipe(
      switchMap((steps) => {
        return combineLatest(steps.map((step) => (step.id ? this.getStep(step.id.toString()) : of(undefined))));
      }),
      map((steps) => {
        const filteredSteps = steps.filter((step) => step !== undefined) as StepDTO[];
        return flattenSteps(filteredSteps);
      }),
      map((flat) => Array.from(new Map(flat.map((s) => [s.navigationPath, s])).values())),
    );
  }

  public getStep(stepId: string) {
    const url = `${environment.APIURL}/steps/${stepId}`;
    return this.http.get<StepDTO>(url);
  }

  public getCompaniesTextOverrides$(companyId: string, lang: string) {
    const url = `${environment.APIURL}/company/${companyId}/frontendTexts?lang=${lang}`;
    return this.http.get<FrontendTextDTO[]>(url);
  }
}
